import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'

function CrfPreview({ visible, onHide, fileUrl, jsonString, orderId, onSave }) {
  const parsedJson = useMemo(() => {
    if (!jsonString) return null
    try {
      return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
    } catch (e) {
      return null
    }
  }, [jsonString])

  const dataRoot = useMemo(() => {
    if (!parsedJson) return null
    // If API sent wrapper with { orderid, file, ..., json: { ... } }
    if (parsedJson && typeof parsedJson === 'object' && parsedJson.json && typeof parsedJson.json === 'object') {
      return parsedJson.json
    }
    return parsedJson
  }, [parsedJson])

  const [isEditing, setIsEditing] = useState(false)
  const [draftRoot, setDraftRoot] = useState(null)
  const [expanded, setExpanded] = useState({})
  const [pendingAdds, setPendingAdds] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const toastRef = useRef(null)

  useEffect(() => {
    if (dataRoot && typeof dataRoot === 'object') {
      const init = {}
      Object.keys(dataRoot).forEach((k) => { init[k] = true })
      setExpanded(init)
    } else {
      setExpanded({})
    }
  }, [dataRoot])

  // Restore saved draft data when modal opens
  useEffect(() => {
    if (visible && dataRoot && !isEditing) {
      const finalOrderId = orderId || (parsedJson && parsedJson.orderid)
      if (finalOrderId) {
        const savedKey = `crf_draft_${finalOrderId}`
        const saved = localStorage.getItem(savedKey)
        if (saved) {
          try {
            const parsed = JSON.parse(saved)
            setDraftRoot(parsed)
            setIsEditing(true)
          } catch (e) {
            // Invalid saved data, ignore
          }
        }
      }
    }
  }, [visible, orderId, parsedJson, dataRoot, isEditing])

  const toggleSection = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const expandAll = () => {
    if (!dataRoot) return
    const all = {}
    Object.keys(dataRoot).forEach((k) => { all[k] = true })
    setExpanded(all)
  }

  const collapseAll = () => {
    if (!dataRoot) return
    const all = {}
    Object.keys(dataRoot).forEach((k) => { all[k] = false })
    setExpanded(all)
  }

  const beginEdit = () => {
    if (dataRoot) setDraftRoot(JSON.parse(JSON.stringify(dataRoot)))
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setDraftRoot(null)
    // Clear saved draft from localStorage when canceling
    const finalOrderId = orderId || (parsedJson && parsedJson.orderid)
    if (finalOrderId) {
      const savedKey = `crf_draft_${finalOrderId}`
      localStorage.removeItem(savedKey)
    }
  }

  const updateAtPath = useCallback((path, nextValue) => {
    setDraftRoot((prev) => {
      const clone = JSON.parse(JSON.stringify(prev || {}))
      let cursor = clone
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i]
        if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {}
        cursor = cursor[key]
      }
      cursor[path[path.length - 1]] = nextValue
      return clone
    })
  }, [])

  const updateEdit = () => {
    if (!draftRoot) return
    const finalOrderId = orderId || (parsedJson && parsedJson.orderid)
    if (finalOrderId) {
      const savedKey = `crf_draft_${finalOrderId}`
      try {
        localStorage.setItem(savedKey, JSON.stringify(draftRoot))
        toastRef.current && toastRef.current.show({ severity: 'success', summary: 'Updated', detail: 'All values saved locally', life: 2000 })
      } catch (e) {
        toastRef.current && toastRef.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to save locally' })
      }
    } else {
      toastRef.current && toastRef.current.show({ severity: 'warn', summary: 'Warning', detail: 'Order ID not found' })
    }
  }

  const saveEdit = async () => {
    if (!draftRoot) return
    const finalOrderId = orderId || (parsedJson && parsedJson.orderid)
    const payload = { order_id: finalOrderId, json_data: draftRoot }
    setIsSaving(true)
    try {
      const res = await fetch(`${process.env.REACT_APP_CRF}update-crf-by-orderid/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res || !res.ok) throw new Error('Failed to update')
      
      // Clear saved draft from localStorage after successful API save
      if (finalOrderId) {
        const savedKey = `crf_draft_${finalOrderId}`
        localStorage.removeItem(savedKey)
      }
      
      toastRef.current && toastRef.current.show({ severity: 'success', summary: 'Saved', detail: 'CRF updated successfully', life: 2000 })
      if (typeof onSave === 'function') {
        try { onSave() } catch (_) {}
      }
      setIsEditing(false)
      setDraftRoot(null)
      setPendingAdds({})
      // Slight delay so the toast is visible before closing the modal
      if (typeof onHide === 'function') setTimeout(() => onHide(), 900)
    } catch (e) {
      toastRef.current && toastRef.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to update CRF' })
    } finally {
      setIsSaving(false)
    }
  }

  const isPlainObject = (val) => val && typeof val === 'object' && !Array.isArray(val)
  const hasNestedObject = (obj) => {
    if (!isPlainObject(obj)) return false
    return Object.values(obj).some((v) => isPlainObject(v))
  }

  const toTitle = (key) => String(key).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())

  const isDrugDoseSection = (sectionKey, value) => {
    if (!sectionKey) return false
    return String(sectionKey).toLowerCase() === 'drug_dose' && isPlainObject(value)
  }

  const addDrugDoseRow = (basePath, currentObj) => {
    ensureEditingWithDraft()
    const keys = Object.keys(currentObj || {})
    let maxIdx = 0
    keys.forEach((k) => {
      const m = k.match(/(\d+)$/)
      if (m) {
        const n = parseInt(m[1], 10)
        if (!Number.isNaN(n)) maxIdx = Math.max(maxIdx, n)
      }
    })
    const newKey = `drug_dos_${maxIdx + 1}`
    const newRow = { drug_name: '', dose: '', duration: '' }
    updateAtPath([...basePath, newKey], newRow)
  }

  const deleteDrugDoseRow = (basePath, rowKey) => {
    ensureEditingWithDraft()
    setDraftRoot((prev) => {
      if (!prev) return prev
      const clone = JSON.parse(JSON.stringify(prev))
      let cursor = clone
      for (let i = 0; i < basePath.length - 1; i++) {
        const key = basePath[i]
        if (!cursor[key] || typeof cursor[key] !== 'object') return prev
        cursor = cursor[key]
      }
      const lastKey = basePath[basePath.length - 1]
      if (cursor[lastKey] && typeof cursor[lastKey] === 'object') {
        delete cursor[lastKey][rowKey]
      }
      return clone
    })
  }

  const renderDrugDoseTable = (value, basePath = []) => {
    const obj = value || {}
    const entries = Object.entries(obj).filter(([, v]) => isPlainObject(v))
    return (
      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee', width: '30%' }}>Drug Name</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee', width: '20%' }}>Dose</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee', width: '20%' }}>Duration</th>
              {isEditing && (
                <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #eee', width: '10%' }}>Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={isEditing ? 4 : 3} style={{ padding: '8px', color: '#666' }}>No rows. Click "Add Row" to create one.</td>
              </tr>
            ) : (
              entries.map(([rowKey, rowObj]) => {
                const nameVal = rowObj?.drug_name ?? ''
                const doseVal = rowObj?.dose ?? ''
                const durationVal = rowObj?.duration ?? ''
                return (
                  <tr key={rowKey}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={String(nameVal)}
                          onChange={(e) => updateAtPath([...basePath, rowKey, 'drug_name'], e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      ) : (
                        nameVal === '' ? '-' : String(nameVal)
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={String(doseVal)}
                          onChange={(e) => updateAtPath([...basePath, rowKey, 'dose'], e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      ) : (
                        doseVal === '' ? '-' : String(doseVal)
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={String(durationVal)}
                          onChange={(e) => updateAtPath([...basePath, rowKey, 'duration'], e.target.value)}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
                        />
                      ) : (
                        durationVal === '' ? '-' : String(durationVal)
                      )}
                    </td>
                    {isEditing && (
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6', textAlign: 'center' }}>
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          className="p-button-danger p-button-text p-button-sm"
                          onClick={() => deleteDrugDoseRow(basePath, rowKey)}
                          title="Delete row"
                        />
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        <div style={{ marginTop: 10 }}>
          <Button
            type="button"
            label="Add Row"
            icon="pi pi-plus"
            className="p-button-text p-button-sm"
            onClick={() => addDrugDoseRow(basePath, obj)}
          />
        </div>
      </div>
    )
  }

  const renderKeyValueTable = (obj, basePath = []) => {
    const rows = Object.entries(obj).filter(([, v]) => !isPlainObject(v))
    if (rows.length === 0) return null
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee', width: '45%' }}>Key</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee' }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, v]) => {
            const path = [...basePath, k]
            const value = isEditing ? (draftRoot ? path.reduce((acc, cur) => (acc ? acc[cur] : undefined), draftRoot) : v) : v
            return (
              <tr key={k}>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6', color: '#333' }}>{toTitle(k)}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6', color: '#555' }}>
                  {isEditing ? (
                    <input
                      type="text"
                      value={value === null || value === undefined ? '' : String(value)}
                      onChange={(e) => updateAtPath(path, e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
                    />
                  ) : (
                    value === null || value === undefined || value === '' ? '-' : String(value)
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  const renderNestedObjects = (obj, level = 1, basePath = []) => {
    return Object.entries(obj)
      .filter(([, v]) => isPlainObject(v))
      .map(([k, v]) => (
        <div key={k} style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 600, color: '#2a2a2a', marginBottom: 8, fontSize: level === 1 ? 14 : 13 }}>
            {toTitle(k)}
          </div>
          {renderKeyValueTable(v, [...basePath, k])}
          {renderNestedObjects(v, level + 1, [...basePath, k])}
          {!hasNestedObject(v) ? renderAddMoreArea([...basePath, k], v) : null}
        </div>
      ))
  }

  const pathKeyFrom = (path = []) => path.join('.')

  const ensureEditingWithDraft = () => {
    if (!isEditing) {
      beginEdit()
    } else if (!draftRoot && dataRoot) {
      setDraftRoot(JSON.parse(JSON.stringify(dataRoot)))
    }
  }

  const handleStartAdd = (path) => {
    ensureEditingWithDraft()
    const key = pathKeyFrom(path)
    setPendingAdds((prev) => ({ ...prev, [key]: { key: '', value: '' } }))
  }

  const handleCancelAdd = (path) => {
    const key = pathKeyFrom(path)
    setPendingAdds((prev) => {
      const copy = { ...prev }
      delete copy[key]
      return copy
    })
  }

  const handleChangeAddField = (path, field, val) => {
    const key = pathKeyFrom(path)
    setPendingAdds((prev) => ({ ...prev, [key]: { ...(prev[key] || { key: '', value: '' }), [field]: val } }))
  }

  const handleConfirmAdd = (path) => {
    const key = pathKeyFrom(path)
    const pending = pendingAdds[key]
    if (!pending) return
    const newKey = String(pending.key || '').trim()
    if (!newKey) return
    const newValue = pending.value
    // set value at path[newKey]
    updateAtPath([...path, newKey], newValue)
    // clear pending
    setPendingAdds((prev) => {
      const copy = { ...prev }
      delete copy[key]
      return copy
    })
  }

  const renderAddMoreArea = (path, currentObj) => {
    if (!isPlainObject(currentObj)) return null
    const key = pathKeyFrom(path)
    const pending = pendingAdds[key]
    return (
      <div style={{ marginTop: 10 }}>
        {!pending ? (
          <Button
            type="button"
            label="Add more"
            icon="pi pi-plus"
            className="p-button-text p-button-sm"
            onClick={() => handleStartAdd(path)}
          />
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Key"
              value={pending.key}
              onChange={(e) => handleChangeAddField(path, 'key', e.target.value)}
              style={{ flex: '0 0 220px', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
            />
            <input
              type="text"
              placeholder="Value"
              value={pending.value}
              onChange={(e) => handleChangeAddField(path, 'value', e.target.value)}
              style={{ flex: '1 1 260px', minWidth: 200, boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
            />
            <Button type="button" label="Add" icon="pi pi-check" onClick={() => handleConfirmAdd(path)} />
            <Button type="button" label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => handleCancelAdd(path)} />
          </div>
        )}
      </div>
    )
  }

  const footer = (
    <div className="flex gap-2 justify-content-end">
      {fileUrl ? (
        <Button
          type="button"
          label="Open PDF in New Tab"
          icon="pi pi-external-link"
          className="p-button-text"
          onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
        />
      ) : null}
      {isEditing ? (
        <>
          <Button type="button" label="Update" icon="pi pi-save" onClick={updateEdit} disabled={isSaving} />
          {/* <Button type="button" label="Cancel" icon="pi pi-undo" className="p-button-text" onClick={cancelEdit} disabled={isSaving} /> */}
          <Button type="button" label={isSaving ? 'Saving...' : 'Accepted'} icon="pi pi-check" onClick={saveEdit} disabled={isSaving} />
        </>
      ) : (
        <Button type="button" label="Edit" icon="pi pi-pencil" onClick={beginEdit} disabled={!dataRoot} />
      )}
      <Button type="button" label="Close" icon="pi pi-times" onClick={onHide} disabled={isSaving} />
    </div>
  )

  return (
    <Dialog
      header={`CRF Preview${orderId ? ` - ${orderId}` : ''}`}
      visible={visible}
      style={{ width: '90vw', maxWidth: 1400 }}
      modal
      onHide={onHide}
      draggable={false}
      resizable
      footer={footer}
    >
      <Toast ref={toastRef} />
      <div style={{ display: 'flex', gap: 12, height: '80vh' }}>
        <div style={{ flex: 3, minWidth: 0, border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
          {fileUrl ? (
            <iframe
              title="CRF PDF Preview"
              src={fileUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ padding: 16, color: '#666' }}>No PDF available</div>
          )}
        </div>
        <div style={{ flex: 2, minWidth: 0, border: '1px solid #eee', borderRadius: 8, overflow: 'auto' }}>
          <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
    
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button type="button" label="Expand All" className="p-button-text p-button-sm" onClick={expandAll} disabled={!dataRoot} />
              <Button type="button" label="Collapse All" className="p-button-text p-button-sm" onClick={collapseAll} disabled={!dataRoot} />
              {orderId ? <span style={{ color: '#666' }}>Order: {orderId}</span> : null}
            </div>
          </div>
          <div style={{ padding: 12 }}>
            {!dataRoot ? (
              <div style={{ color: '#666' }}>No JSON data available</div>
            ) : (
              Object.entries(isEditing ? (draftRoot || {}) : dataRoot).map(([section, value]) => (
                <div key={section} style={{ marginBottom: 16 }}>
                  <div
                    onClick={() => toggleSection(section)}
                    style={{ padding: '8px 10px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, fontWeight: 600, color: '#1f2937', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <i className={`pi ${expanded[section] ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: 12 }} />
                    <span>{toTitle(section)}</span>
                  </div>
                  {expanded[section] ? (
                    <div style={{ padding: '10px 6px' }}>
                      {isDrugDoseSection(section, value) ? (
                        renderDrugDoseTable(value, [section])
                      ) : isPlainObject(value) ? (
                        <>
                          {renderKeyValueTable(value, [section])}
                          {renderNestedObjects(value, 1, [section])}
                          {!hasNestedObject(value) ? renderAddMoreArea([section], value) : null}
                        </>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            <tr>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6', width: '45%', color: '#333' }}>Value</td>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f6f6f6', color: '#555' }}>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={value === null || value === undefined ? '' : String(value)}
                                    onChange={(e) => updateAtPath([section], e.target.value)}
                                    style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4 }}
                                  />
                                ) : (
                                  value === null || value === undefined || value === '' ? '-' : String(value)
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default CrfPreview